import { useCallback } from 'react';

import { useToast } from './useToastContext';

export interface PdfTabHandle {
  /** Navigate the tab to an existing URL (e.g., a signed S3 URL). */
  navigateToUrl: (url: string) => void;
  /**
   * Set the tab's content to a freshly fetched PDF blob. Creates a blob
   * URL internally and revokes it after the tab finishes loading.
   */
  setBlob: (blob: Blob) => void;
  /**
   * Close the tab and show a generic "Export Failed" toast. Logs the error
   * to the browser console using `errorContext` for grep-ability.
   */
  fail: (errorContext: string, error: unknown) => void;
  /**
   * Close the tab without showing a toast. Use when the caller wants to
   * surface its own (more specific) error message instead of the generic one.
   */
  close: () => void;
}

const LOADING_BODY =
  '<div style="font-family:system-ui,sans-serif;padding:24px;color:#555">' +
  'Loading print view…' +
  '</div>';

/**
 * Open a PDF in a new browser tab without tripping popup blockers.
 *
 * Returns a function that MUST be called from inside a synchronous click
 * handler. It opens a placeholder tab immediately so the browser treats
 * the popup as user-initiated, then returns a handle the caller can fill
 * in after an awaited fetch. Returns `null` (and shows a "Popup blocked"
 * toast) when the browser refuses to open the tab.
 *
 * Typical usage:
 *
 *   const openPdfTab = usePdfTab();
 *
 *   const handleClick = async () => {
 *     const tab = openPdfTab();
 *     if (!tab) return;
 *     try {
 *       const response = await fetchPdf();
 *       if (!response.data) {
 *         tab.fail('order PDF', new Error('no PDF data received'));
 *         return;
 *       }
 *       tab.setBlob(new Blob([response.data], { type: 'application/pdf' }));
 *     } catch (err) {
 *       tab.fail('order PDF', err);
 *     }
 *   };
 */
export function usePdfTab(): () => PdfTabHandle | null {
  const { error: showError } = useToast();

  return useCallback((): PdfTabHandle | null => {
    const tab = window.open('', '_blank');
    if (!tab) {
      showError(
        'Popup blocked',
        'Your browser blocked the print view popup. Please allow popups for this site and try again.'
      );
      return null;
    }

    tab.document.title = 'Loading print view…';
    tab.document.body.innerHTML = LOADING_BODY;

    return {
      navigateToUrl: (url: string) => {
        tab.location.href = url;
      },
      setBlob: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        let revoked = false;
        const revoke = () => {
          if (revoked) return;
          revoked = true;
          URL.revokeObjectURL(url);
        };
        // Chrome doesn't reliably fire `load` for blob navigations, so pair
        // the listener with a timeout fallback to avoid leaking the blob URL.
        tab.addEventListener('load', revoke, { once: true });
        setTimeout(revoke, 60_000);
        tab.location.href = url;
      },
      fail: (errorContext: string, error: unknown) => {
        console.error(`Error exporting ${errorContext}:`, error);
        tab.close();
        showError('Export Failed', 'Failed to export PDF. Please try again.');
      },
      close: () => {
        tab.close();
      },
    };
  }, [showError]);
}
